/**
 * The buy-state derivation shared by the two product cards.
 *
 * `ProductCard.tsx` (desktop grid) and `ProductCardMobile.tsx` (phone list row)
 * had this block duplicated verbatim — 22 lines each, differing only in that the
 * desktop card also computed a description preview. Two copies of "can this be
 * added to the cart" is the kind of duplication that goes wrong quietly: a
 * pricing rule fixed in one card and not the other shows the customer a working
 * Add-to-cart button on one breakpoint and an Enquire button on the other.
 *
 * This module emits NO markup, which is what makes lifting it safe: the cards'
 * rendered HTML is a pure function of these booleans, so moving the arithmetic
 * cannot move a pixel. The genuinely divergent parts of the two cards — the
 * shadcn `Card`/`AspectRatio` shell vs. plain `div`s, and every class string —
 * deliberately stay in their own files.
 */

import { formatPrice } from "@/lib/format-price";
import { variantPriceRange, isPurchasable } from "@/lib/product-pricing";
import type { Product } from "@/stores/product-store";

export interface ProductCardModel {
  /** Purchasable self-service: not enquire-only AND a price resolves. */
  priceable: boolean;
  hasVariants: boolean;
  /**
   * Quick-add is for SIMPLE products only. A product with variants routes to
   * the detail drawer instead so the customer picks an option — see the
   * `Choose options` branch in `ProductCardActions`.
   */
  canAddToCart: boolean;
  /**
   * What the card prints where the price goes: `From £X` when options span a
   * range, the single price when they don't, and `Enquire` when nothing is
   * priceable — never `£0.00` and never a bare blank.
   */
  priceLabel: string;
  /**
   * Out of stock AND orderable in principle. Enquire-only products are excluded
   * because `inStock` is meaningless for them (they never had a buy box), and
   * labelling a bespoke commission "Sold out" reads as a mistake.
   */
  isOutOfStock: boolean;
}

/** Derive every buy-state flag both product cards render from. */
export function deriveProductCardModel(product: Product): ProductCardModel {
  const variants = product.variants ?? [];
  const { min: fromPence, max: toPence } = variantPriceRange(
    product.pricePence,
    variants
  );
  const priceable = isPurchasable(
    product.deliveryTiming,
    product.pricePence,
    variants
  );
  const hasVariants = variants.length > 0;
  const canAddToCart = priceable && product.inStock >= 1 && !hasVariants;
  const priceLabel =
    fromPence != null
      ? fromPence !== toPence
        ? `From ${formatPrice(fromPence)}`
        : formatPrice(fromPence)
      : "Enquire";
  const isOutOfStock =
    product.inStock < 1 && product.deliveryTiming !== "enquireOnly";
  return { priceable, hasVariants, canAddToCart, priceLabel, isOutOfStock };
}
